'use client';

import { type ReactNode, useState } from 'react';
import Tabs from 'components/controls/tabs';
import styles from './tab-drawer.module.scss';

const TabDrawers = ({
	tabs,
	inputGroupName,
	screenreaderLegendText,
	orientation = 'horizontal',
	tabNameAlignment = 'start',
	hasDivider = true,
	showTabs = true,
	tabBodyWrapperClass,
	initialActiveTab,
	onTabChange,
}: Props) => {
	const [activeTab, setActiveTab] = useState(
		() =>
			(initialActiveTab && tabs.find((tab) => tab.name === initialActiveTab)) ??
			tabs?.[0],
	);

	if (!tabs?.length) {
		return null;
	}

	const handleClick = (e) => {
		const tabValue = e.currentTarget.value;
		const newActiveTab = tabs.find((tab) => tab.name === tabValue);
		setActiveTab(newActiveTab);
		onTabChange?.(newActiveTab.name);
	};

	return (
		<div
			className={[
				styles.wrapper,
				orientation === 'vertical' ? styles.vertical : '',
			].join(' ')}
		>
			{showTabs && (
				<Tabs
					smallTabs={true}
					tabNames={tabs.map((tab) => tab.name)}
					handleTabChange={handleClick}
					activeTab={activeTab.name}
					inputGroupName={inputGroupName}
					screenreaderLegendText={screenreaderLegendText}
					tabNameAlignment={tabNameAlignment}
					orientation={orientation}
				/>
			)}
			<div
				className={[
					tabBodyWrapperClass ?? styles.bodyWrapper,
					hasDivider && showTabs ? styles.divider : '',
				].join(' ')}
				key={activeTab.name}
			>
				{activeTab.body}
			</div>
		</div>
	);
};

export type Props = {
	inputGroupName?: string;
	screenreaderLegendText: string;
	tabs: Array<TabProps>;
	hasDivider?: boolean;
	showTabs?: boolean;
	orientation?: 'horizontal' | 'vertical';
	tabNameAlignment?: 'start' | 'center' | 'end';
	initialActiveTab?: string;
	tabBodyWrapperClass?: string;
	onTabChange?: (tabName: string) => void;
};

type TabProps = {
	name: string;
	body: ReactNode;
};

export default TabDrawers;
